// src/components/store/sections/ai-recommender/AiRecommenderDoorHanger.tsx
"use client";

import { useState, useRef, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { 
  Sparkles, 
  ShoppingCart, 
  Zap, 
  Check, 
  ChevronLeft, 
  ChevronRight, 
  ShoppingBag, 
  Loader2,
  ArrowRight
} from "lucide-react";
import { useCart } from "@/context/CartContext";

interface ProductItem {
  id: number;
  name: string;
  slug: string;
  price: number | string;
  compare_price?: number | string | null;
  primary_image?: string | null;
  image?: string | null;
  description?: string;
  category_name?: string;
  unit?: string;
}

interface AiRecommenderProps {
  products?: ProductItem[];
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  subdomain?: string;
}

// 🌟 স্ক্রিনশট ১ ও ২ অনুযায়ী ১০টি ডোর হ্যাঙ্গার কালার থিম
const HANGER_THEMES = [
  {
    gradient: "from-[#00b050] to-[#007a37]",
    accent: "#00b050",
    shadow: "shadow-emerald-500/20",
    badge: "Special Pick",
  },
  {
    gradient: "from-[#0072ce] to-[#004a87]",
    accent: "#0072ce",
    shadow: "shadow-blue-500/20",
    badge: "Trending",
  },
  {
    gradient: "from-[#f59e0b] to-[#d97706]",
    accent: "#f59e0b",
    shadow: "shadow-amber-500/20",
    badge: "Hot Deal",
  },
  {
    gradient: "from-[#e11d48] to-[#9f1239]",
    accent: "#e11d48",
    shadow: "shadow-rose-500/20",
    badge: "Best Seller",
  },
  {
    gradient: "from-[#0284c7] to-[#075985]",
    accent: "#0284c7",
    shadow: "shadow-sky-500/20",
    badge: "Verified",
  },
  {
    gradient: "from-[#7c3aed] to-[#5b21b6]",
    accent: "#7c3aed",
    shadow: "shadow-purple-500/20",
    badge: "Top Rated",
  },
];

export default function AiRecommenderDoorHanger({
  products = [],
  title = "Recommended For You",
  subtitle = "AI-powered curated collection crafted exclusively for your preferences",
  primaryColor = "#059669",
  subdomain = "nextlook",
}: AiRecommenderProps) {
  const router = useRouter();
  const { addToCart } = useCart();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [addedIds, setAddedIds] = useState<Record<number, boolean>>({});
  const [buyingId, setBuyingId] = useState<number | null>(null);

  const resolveImageUrl = useCallback(
    (url?: string | null) => {
      if (!url) return null;
      const path = url.trim();
      if (path.startsWith("http://") || path.startsWith("https://")) return path;
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      const base = isLocal ? `http://${subdomain}.localhost:8000` : "";
      return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
    },
    [subdomain]
  );

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // 🌟 ADD TO CART বাটন হ্যান্ডলার
  const handleAddToCart = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setAddedIds((prev) => ({ ...prev, [prod.id]: true }));
    setTimeout(() => {
      setAddedIds((prev) => ({ ...prev, [prod.id]: false }));
    }, 2000);

    const fullImg = resolveImageUrl(prod.primary_image || prod.image);
    const itemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        let list: any[] = [];
        if (raw) {
          try {
            list = JSON.parse(raw);
          } catch {}
        }
        if (!Array.isArray(list)) list = [];
        const idx = list.findIndex((x: any) => String(x.id) === String(prod.id));
        if (idx > -1) {
          list[idx].quantity = (Number(list[idx].quantity) || 1) + 1;
        } else {
          list.push({ ...itemObj });
        }
        localStorage.setItem(key, JSON.stringify(list));
      } catch {}
    });

    if (typeof addToCart === "function") {
      addToCart(itemObj, 1);
    }

    window.dispatchEvent(new CustomEvent("addToCart", { detail: itemObj }));
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent("openCartDrawer"));
  };

  // 🌟 BUY NOW বাটন হ্যান্ডলার
  const handleBuyNow = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setBuyingId(prod.id);
    const fullImg = resolveImageUrl(prod.primary_image || prod.image);

    const singleItemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      title: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    try {
      sessionStorage.setItem("direct_checkout_item", JSON.stringify(singleItemObj));
    } catch {}

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        localStorage.setItem(key, JSON.stringify([singleItemObj]));
      } catch {}
    });

    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));

    router.push(`/checkout?buy_now=true&product_id=${prod.id}`);
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
      {/* 🌟 হেডার */}
      <div className="flex items-center justify-between mb-8 pb-3 border-b border-slate-100">
        <div>
          <span
            style={{ color: primaryColor }}
            className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5 font-mono"
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" /> AI SMART RECOMMENDATIONS
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link
            href="/products"
            className="text-xs font-bold text-slate-600 hover:text-emerald-600 transition-colors hidden sm:flex items-center gap-1 mr-2"
          >
            <span>View All</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handleScroll("left")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer hover:border-slate-300"
              aria-label="Scroll Left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleScroll("right")}
              className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center shadow-xs transition-all active:scale-95 cursor-pointer hover:border-slate-300"
              aria-label="Scroll Right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 🌟 ডোর হ্যাঙ্গার হরিজন্টাল স্ক্রল ট্র্যাক */}
      <div
        ref={scrollRef}
        className="flex items-stretch gap-6 overflow-x-auto pt-4 pb-8 px-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {products.map((item, idx) => {
          const theme = HANGER_THEMES[idx % HANGER_THEMES.length];
          const rawImg = item.primary_image || item.image;
          const imgUrl = resolveImageUrl(rawImg);
          const priceVal = typeof item.price === "number" ? item.price : parseFloat(String(item.price) || "0");
          const isAdded = addedIds[item.id];
          const isBuying = buyingId === item.id;
          const targetUrl = `/products/${item.slug || item.id}`;

          return (
            <div
              key={item.id}
              className="w-60 sm:w-64 shrink-0 group relative flex flex-col justify-between bg-white rounded-[38px] border-2 border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden select-none"
            >
              {/* 🌟 ১. ডোর হ্যাঙ্গার টপ স্লট ও সার্কেল কাটআউট (SVG রেন্ডার) */}
              <div className="relative w-full pt-4 pb-2 flex flex-col items-center">
                <span className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-300 font-mono">
                  DOOR HANGER
                </span>

                {/* হুবহু স্ক্রিনশটের মতো ডোর হ্যাঙ্গার হোল ও বাম পাশের স্লট */}
                <div className="relative w-full h-20 flex items-center justify-center mt-1">
                  {/* বাম পাশের হরাইজন্টাল স্লট বার */}
                  <div className="absolute left-0 w-[55%] h-4 bg-slate-100/90 rounded-r-full shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)] border-y border-slate-200/50" />
                  
                  {/* মাঝের সার্কুলার হ্যাঙ্গার হোল */}
                  <div className="w-18 h-18 rounded-full bg-slate-100/90 shadow-[inset_0_4px_8px_rgba(0,0,0,0.18)] border border-slate-200/60 flex items-center justify-center relative z-10">
                    <div className="w-full h-full rounded-full bg-gradient-to-b from-slate-200/40 via-transparent to-white/80" />
                  </div>
                </div>
              </div>

              {/* 🌟 ২. ডায়াগোনাল কালার স্লাইস ব্যাকগ্রাউন্ড */}
              <div
                className={`absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-b ${theme.gradient} transition-transform duration-300 group-hover:scale-[1.02]`}
                style={{
                  clipPath: "polygon(0% 28%, 100% 0%, 100% 100%, 0% 100%)",
                }}
              />

              {/* 🌟 ৩. ভাসমান প্রোডাক্ট ইমেজ (ক্লিক করলে ডিটেইলস পেজে যাবে) */}
              <div className="relative z-10 px-4 -mt-3 mb-2 flex flex-col items-center">
                <Link href={targetUrl} className="block w-full cursor-pointer">
                  <div className="relative w-full h-40 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-2">
                    {imgUrl ? (
                      <div className="relative w-36 h-36">
                        <Image
                          src={imgUrl}
                          alt={item.name}
                          fill
                          unoptimized
                          className="object-contain drop-shadow-[0_16px_20px_rgba(0,0,0,0.35)]"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-2xl bg-white/80 shadow-md flex items-center justify-center text-slate-300">
                        <ShoppingBag className="w-10 h-10" />
                      </div>
                    )}
                  </div>
                </Link>
                {/* গ্রাউন্ড শ্যাডো */}
                <div className="w-28 h-2.5 rounded-[100%] bg-black/30 blur-xs -mt-2 pointer-events-none" />
              </div>

              {/* 🌟 ৪. বটম টাইপোগ্রাফি ও তথ্য (স্ক্রিনশটের স্টাইল) */}
              <div className="relative z-10 px-5 pb-5 pt-1 text-center text-white flex flex-col justify-end space-y-3">
                <div className="space-y-0.5">
                  <span className="text-xs font-serif italic tracking-wide text-white/90 block">
                    Best Quality
                  </span>
                  <Link href={targetUrl} className="block cursor-pointer">
                    <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-white truncate drop-shadow-sm">
                      {item.name}
                    </h3>
                  </Link>
                  <p className="text-[10px] text-white/75 line-clamp-1 leading-snug px-1">
                    {item.description || "Authentic quality verified item with doorstep delivery"}
                  </p>
                </div>

                {/* প্রাইস ট্যাগ */}
                <div className="py-1">
                  <span className="text-2xl font-black tracking-tight text-white drop-shadow-md">
                    ৳{Math.round(priceVal).toLocaleString()}
                  </span>
                </div>

                {/* 🌟 ৫. অ্যাকশন বাটনসমূহ (সম্পূর্ণ সক্রিয়) */}
                <div className="space-y-1.5 pt-1">
                  <button
                    type="button"
                    onClick={(e) => handleAddToCart(e, item)}
                    className="w-full py-2.5 px-3 rounded-full bg-white text-slate-900 font-black text-[11px] uppercase tracking-wider shadow-md hover:bg-slate-100 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    {isAdded ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-emerald-700">ADDED</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-3.5 h-3.5 text-slate-900" />
                        <span>ADD TO CART</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleBuyNow(e, item)}
                    className="w-full py-2 px-3 rounded-full bg-black/40 hover:bg-black/60 text-white font-black text-[10px] uppercase tracking-wider backdrop-blur-xs transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1"
                  >
                    {isBuying ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3 fill-current text-amber-300" />
                    )}
                    <span>{isBuying ? "PLEASE WAIT..." : "BUY NOW"}</span>
                  </button>
                </div>

                {/* ডোর হ্যাঙ্গার বটম ফুটনোট */}
                <span className="text-[8px] font-mono tracking-widest text-white/60 uppercase block pt-1">
                  DO NOT DISTURB
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}